import Vector from "victor";
import Rect from "../types/Rect";
import Camera from "../Camera";
import { Shape2 } from "../types/Shape";
import { TextShape2 } from "./TextShape";
import "../quill.snow.css";
import WhiteboardControllerInterface from "../types/WhiteboardControllerInterface";
import ToolType from "../types/ToolType";
import { EraserShape2 } from "../pen/PenShape";
import { ShapeDOMRepresentation } from "../types/ShapeDOMRepresentation";
import { QuillHelper } from "./QuillHelper";
import { Scope } from "../types/Scope";
import { AttributeKey } from "../types/attributes";
import { RangeStatic } from "quill";

export const TEXT_MARGIN = 20;

export default class TextShapeDOMRepresentation
  implements ShapeDOMRepresentation
{
  id: string;
  shape: TextShape2;
  el: HTMLDivElement;
  camera: Camera;
  wb: WhiteboardControllerInterface;
  quillHelper: QuillHelper;

  constructor(
    wb: WhiteboardControllerInterface,
    id: string,
    shape: TextShape2,
    transformEl: HTMLElement,
    camera: Camera
  ) {
    this.wb = wb;
    this.el = document.createElement("div");
    // this.el.dataset.shape = JSON.stringify(shape);

    this.camera = camera;
    this.shape = shape as TextShape2;
    this.id = id;

    transformEl.appendChild(this.el);

    this.quillHelper = new QuillHelper(wb, this.id, this.el, shape, {
      quillHelperWantsToSetParentElementClassName: (className) =>
        (this.el.className = className),
      quillHelperWantsToSetParentElementUserSelect: (value) =>
        (this.el.style.userSelect = value),
      quillHelperWantsToDelete: () =>
        this.wb.shapeStore.sendDelete(Scope.Persist, this.id),
      quillHelperWantsToSave: (scope) => this.save(scope),
    });
    this.applyTransform();
    if (
      this.wb.tool?.toolType !== ToolType.Text &&
      this.wb.tool?.toolType !== ToolType.Select
    ) {
      this.quillHelper.quill.disable();
    }
  }
  teardown() {
    this.el.parentElement?.removeChild(this.el);
    this.quillHelper.teardown();
  }

  save(scope = Scope.Persist) {
    this.wb.shapeStore.sendUpsertSingle(scope, this.id, {
      key: AttributeKey.Text,
      value: this.quillHelper.quill.getContents(),
    });
  }

  getSelection(): RangeStatic | null {
    return this.quillHelper.quill.getSelection();
  }

  setSelection(selection: RangeStatic) {
    this.quillHelper.quill.setSelection(selection);
  }

  get maySelect(): boolean {
    return true;
  }

  get boundingRect(): Rect {
    const bottomRight = new Vector(
      this.el.clientWidth,
      this.el.clientHeight
    ).add(Vector.fromArray(this.shape.boundingBoxOrigin));
    return new Rect(
      Vector.fromArray(this.shape.boundingBoxOrigin).add(
        Vector.fromArray(this.shape.translate)
      ),
      bottomRight.add(Vector.fromArray(this.shape.translate))
    );
  }

  get selectionRect(): Rect {
    return this.boundingRect;
  }

  // eslint-disable-next-line
  applyEraser(shape: EraserShape2, id: string) {
    /* do nothing */
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  removeEraser(shape: EraserShape2, id: string) {
    /* do nothing */
  }

  // eslint-disable-next-line
  hitTest(worldSpacePoint: Vector, isLoose: boolean): boolean {
    return this.boundingRect.contains(worldSpacePoint);
  }

  hitTestRect(rect: Rect): boolean {
    return this.selectionRect.intersects(rect);
  }

  applyTransform() {
    // Element is positioned in world coordinates using CSS transform
    const origin = this.boundingRect.min;
    const newTransform = `translate3d(${origin.x}px, ${origin.y}px, 0)`;
    if (this.el.style.translate !== newTransform)
      this.el.style.transform = newTransform;
    this.quillHelper.applyMaxWidth();
  }

  // eslint-disable-next-line
  update(newShape: Shape2, isLive = true) {
    this.shape = newShape as TextShape2;
    this.quillHelper.update(this.shape, isLive);
    this.applyTransform();
  }

  occlude(viewportRect: Rect) {
    const oldValue = this.el.style.display;
    const newValue = this.boundingRect.intersects(viewportRect)
      ? "block"
      : "none";
    if (oldValue !== newValue) {
      this.el.style.display = newValue;
    }
  }

  // Canvas rendering

  applyCanvasTransformToCanvas(ctx: CanvasRenderingContext2D) {
    // Text is never scaled, so just use the origin
    const origin = this.boundingRect.min;
    ctx.translate(origin.x - TEXT_MARGIN, origin.y - TEXT_MARGIN); // #TextMarginHack
  }

  renderShapeToCanvas(scaleFactor: number): HTMLCanvasElement {
    const canvas = document.createElement("canvas");
    // add 20px on each side in case of random minor differences
    canvas.width = (this.el.clientWidth + TEXT_MARGIN * 2) * scaleFactor; // #TextMarginhack
    canvas.height = (this.el.clientHeight + TEXT_MARGIN * 2) * scaleFactor;

    const ctx = canvas.getContext("2d");
    if (!ctx) return canvas;

    ctx.save();
    ctx.translate(TEXT_MARGIN, TEXT_MARGIN);
    this.quillHelper.renderShapeToCanvas(ctx, scaleFactor);
    ctx.restore();
    return canvas;
  }
}
